import {
  Icon,
  Label,
  MessageBar,
  MessageBarType,
  Stack,
  Toggle,
  TooltipHost,
} from "@fluentui/react";
import { stackTokens, styles } from "./experimentDetailsForm.styles";
import { BaseTextField, BaseTagField, FormRow } from "../FormComponents";
import { ExperimentFormContext } from "../../../Hooks/UseExperimentForm";
import { useMemo } from "react";
import { useFetchBaseJsonConfigAll } from "../../../Hooks/AdoHooks/UseFetchBaseJsonConfigAll";
import BaseTagDropdownField from "../FormComponents/BaseTagDropdownField";
import { useAppContext } from "../../../Contexts";

interface ExperimentDetailsFormProps {
  /**
   * Form context containing form state, handlers, and validation logic
   */
  experimentFormContext: ExperimentFormContext;
  /**
   * Validation errors keyed by field name. Used to show error indicator/counts on tabs.
   */
  errors?: Record<string, string[]>;
  /**
   * When true, forms are editable. When false, forms are read-only.
   */
  isEditMode?: boolean;
}

/**
 * Form for editing the main experiment details such as name, key, description, extension filters, and analysis tags.
 */
const ExperimentDetailsForm: React.FC<ExperimentDetailsFormProps> = ({
  experimentFormContext,
  isEditMode = false,
  errors = {},
}) => {
  const { extensionConfig } = useAppContext();
  const { formState, tags, handleFieldChange } = experimentFormContext;

  const extensionPrefix = useMemo(
    () =>
      formState.enableExtensionPrefix
        ? experimentFormContext.extensionPrefix
        : undefined,
    [formState.enableExtensionPrefix, experimentFormContext.extensionPrefix],
  );

  const { value: baseJsonFiles = [], loading } = useFetchBaseJsonConfigAll();

  const isBaseConfigSet = Boolean(extensionConfig?.extensionFilters);

  const experimentOptions = Array.from(
    new Set(
      baseJsonFiles.map(({ extensionFilters }) => extensionFilters).flat(),
    ),
  ).filter((x): x is string => Boolean(x));

  return (
    <Stack className={styles.formContainer} tokens={stackTokens}>
      {formState.enableExtensionPrefix && (
        <MessageBar messageBarType={MessageBarType.info}>
          Extension prefix is enabled for this experiment. Make sure to include
          the prefix {`'${experimentFormContext.extensionPrefix}'`} when
          referencing the variable name in your code.
        </MessageBar>
      )}
      {/* Row 2: Feature Name, Feature Key */}
      <FormRow horizontal>
        <BaseTextField
          fieldKey="featureName"
          label="Feature Name"
          subtitle="Friendly name of the feature experiment"
          value={formState.featureName}
          isRequired={true}
          isEdit={isEditMode}
          onChange={handleFieldChange}
          errorMessage={errors.featureName || undefined}
          grow
        />
        <Stack grow horizontal verticalAlign="end" tokens={{ childrenGap: 10 }}>
          <BaseTextField
            fieldKey="featureKey"
            label="Feature Key"
            subtitle="Unique identifier for the experiment"
            value={formState.featureKey}
            isRequired={true}
            isEdit={isEditMode}
            onChange={handleFieldChange}
            errorMessage={errors.featureKey || undefined}
            prefix={extensionPrefix}
          />
          <Stack verticalAlign="center" className={styles.extensionPrefix}>
            <Toggle
              checked={formState.enableExtensionPrefix}
              label={<EnableExtensionPrefixToggleLabel />}
              onChange={(_, checked) =>
                handleFieldChange("enableExtensionPrefix", !!checked)
              }
              disabled={!isEditMode}
              onText="enabled"
              offText="disabled"
            />
          </Stack>
        </Stack>
      </FormRow>

      {/* Feature Description */}
      <BaseTextField
        fieldKey="featureDescription"
        label="Feature Description"
        value={formState.featureDescription}
        isRequired={true}
        isEdit={isEditMode}
        onChange={handleFieldChange}
        errorMessage={errors.featureDescription || undefined}
        multiline
        rows={3}
        grow
      />

      {/* Extension Filters */}
      <BaseTagDropdownField
        fieldKey="extensionFilters"
        label="Extension Filters"
        selectedOptions={formState.extensionFilters}
        options={experimentOptions}
        onChange={handleFieldChange}
        isEdit={isEditMode}
        subtitle={
          <span>
            Array of extension names for audience filtering.{" "}
            {isBaseConfigSet && (
              <span>
                Optional, extension filters defined in base config as{" "}
                <code>{extensionConfig?.extensionFilters?.join(", ")}</code>
              </span>
            )}
          </span>
        }
        placeholder="Add extension filter..."
        grow
        isRequired={!isBaseConfigSet}
        loading={loading}
        errorMessage={errors.extensionFilters || undefined}
      />

      {/* Analysis Tags */}
      <BaseTagField
        fieldKey="analysisTags"
        label="Analysis Tags"
        selectedItems={tags}
        isRequired={false}
        isEdit={isEditMode}
        placeholder="Add tag..."
        subtitle="2D array for analysis rules setup. Each sub-array corresponds to a specific stamp. Prod has 5 stamps, MPAC has 2 stamps, and RC has 1 stamp."
        onChange={handleFieldChange}
        errorMessage={errors.analysisTags || undefined}
        grow
      />
    </Stack>
  );
};

export const EnableExtensionPrefixToggleLabel: React.FC = () => {
  return (
    <Stack
      horizontal
      verticalAlign="center"
      className={styles.extensionPrefixToggleLabel}
      tokens={{ childrenGap: 5 }}
    >
      <Label>Extension prefix</Label>
      <TooltipHost
        content="Adds extension name prefix to feature key to ensure uniqueness 
                across extensions."
      >
        <Icon iconName="info" />
      </TooltipHost>
    </Stack>
  );
};

export default ExperimentDetailsForm;
